import { Vector3 as ThreeVector3, Quaternion as ThreeQuaternion, PerspectiveCamera, CatmullRomCurve3, Matrix4 } from 'three';
import { Logger } from '../core/Logger';

/**
 * Motion preset — bundles duration, easing and path choice so authors pick a feeling
 * instead of tuning numbers.
 */
export type MotionPreset = 'cinematic' | 'snappy' | 'calm' | 'blend' | 'off';

/** Path shape between two camera poses. */
export type PathMode = 'direct' | 'arc' | 'overview';

interface PresetConfig {
    durationMs: number;
    /** Distance ratio (travel / camera distance) above which the overview arc kicks in. */
    overviewThreshold: number;
    /** How far the overview pulls back, as a factor of the travel distance. */
    overviewLift: number;
}

const PRESETS: Record<Exclude<MotionPreset, 'off'>, PresetConfig> = {
    cinematic: { durationMs: 1600, overviewThreshold: 2.5, overviewLift: 0.45 },
    snappy: { durationMs: 700, overviewThreshold: 4.0, overviewLift: 0.25 },
    calm: { durationMs: 2400, overviewThreshold: 2.0, overviewLift: 0.55 },
    // 'blend' — Alex' Geraetetest 2026-08-03: die deterministische Fahrt wirkte 'arm'
    // gegenueber dem gewachsenen Smoothing. Der Grund ist vermutlich das harte Ende;
    // das asymptotische Nachziehen fuehlt sich lebendig an, weil es nie abrupt stoppt.
    // Dieses Preset fliegt den Bogen, uebergibt aber im letzten Viertel zurueck ans
    // Smoothing — Reise UND weiches Ankommen.
    blend: { durationMs: 1300, overviewThreshold: 2.5, overviewLift: 0.4 },
};

/** Ab diesem Fortschritt uebergibt `blend` zurueck ans Smoothing. */
const BLEND_HANDOFF = 0.78;

/**
 * CameraChoreographer — deterministic, choreographed camera travel between presentation
 * steps.
 *
 * Why this exists: `AdvancedPOICamera` moves the camera with an asymptotic
 * `position.lerp(desired, speed * dt)`. That never actually arrives, has no defined
 * duration and draws a straight line — it reads as soft trailing, not as a journey.
 * A presentation product needs the opposite: a transition that starts, travels along a
 * deliberate path and *ends* within its configured time (product acceptance criterion,
 * Content post 4437).
 *
 * Division of labour: this class owns STEP TRANSITIONS only. While no transition is
 * running the camera keeps its existing smoothing, so free navigation and POI wiggle
 * tracking behave exactly as before.
 *
 * Interruptible by design: starting a new transition mid-flight re-anchors at the
 * camera's current pose, so clicking through steps quickly never snaps.
 */
export class CameraChoreographer {
    private _active = false;
    private _elapsedMs = 0;
    private _durationMs = 0;
    private _pathMode: PathMode = 'direct';
    private _preset: MotionPreset = 'cinematic';

    private _startPos = new ThreeVector3();
    private _startQuat = new ThreeQuaternion();
    private _curve: CatmullRomCurve3 | null = null;

    // Scratch objects — camera code runs every frame, so no per-frame allocations.
    private _scratchPos = new ThreeVector3();
    private _scratchMid = new ThreeVector3();
    private _scratchQuat = new ThreeQuaternion();
    private _scratchMat = new Matrix4();
    private _up = new ThreeVector3(0, 1, 0);

    constructor(preset?: MotionPreset) {
        // Default ist bewusst 'off': Alex' Gerätetest 2026-08-03 — die choreografierte
        // Fahrt wirkte schlechter als das gewachsene Smoothing. Choreografie greift nur
        // noch auf ausdrückliche Anfrage (?motion=...), bis sie nachweislich besser ist.
        this._preset = preset ?? CameraChoreographer.presetFromUrl() ?? 'off';
    }

    /** `?motion=cinematic|snappy|calm|off` — lets a preset be compared without a UI. */
    private static presetFromUrl(): MotionPreset | null {
        try {
            const raw = new URLSearchParams(window.location.search).get('motion');
            if (raw === 'cinematic' || raw === 'snappy' || raw === 'calm' || raw === 'blend' || raw === 'off') return raw;
        } catch { /* no window (SSR/tests) */ }
        return null;
    }

    public get preset(): MotionPreset { return this._preset; }
    public setPreset(preset: MotionPreset): void { this._preset = preset; }

    public isActive(): boolean { return this._active; }

    /**
     * Begin a transition from the camera's CURRENT pose. Call this when the active step
     * changes — the destination is not needed up front, it is read live every frame so a
     * moving/animating target stays tracked.
     *
     * @param travelDistance distance between current and destination pose, used to decide
     *        whether this is a short hop (direct) or a big jump that deserves an overview arc.
     * @param cameraDistance the configured POI viewing distance — the scale reference.
     */
    public begin(camera: PerspectiveCamera, destination: ThreeVector3, cameraDistance: number): void {
        if (this._preset === 'off') { this._active = false; return; }
        const cfg = PRESETS[this._preset];

        this._startPos.copy(camera.position);
        this._startQuat.copy(camera.quaternion);
        this._elapsedMs = 0;
        this._durationMs = cfg.durationMs;
        this._active = true;

        const travel = this._startPos.distanceTo(destination);
        const scale = Math.max(0.0001, cameraDistance);
        const ratio = travel / scale;

        if (ratio < 0.35) {
            // Same neighbourhood — a choreographed arc would look theatrical.
            this._pathMode = 'direct';
            this._curve = null;
        } else if (ratio < cfg.overviewThreshold) {
            this._pathMode = 'arc';
            this._curve = this.buildArc(this._startPos, destination, travel * 0.18);
        } else {
            // The Prezi move: pull back far enough that both ends are comprehensible,
            // travel across, then descend onto the target.
            this._pathMode = 'overview';
            this._curve = this.buildArc(this._startPos, destination, travel * cfg.overviewLift);
        }

        Logger.Instance.Info(
            `transition begin: preset=${this._preset} path=${this._pathMode} ` +
            `travel=${travel.toFixed(3)} ratio=${ratio.toFixed(2)} duration=${this._durationMs}ms`,
            'Choreographer'
        );
    }

    /**
     * Arc through a lifted midpoint. Lift is along world-up plus a slight outward push,
     * which reads as "rising over the scene" rather than "swinging sideways".
     */
    private buildArc(from: ThreeVector3, to: ThreeVector3, lift: number): CatmullRomCurve3 {
        this._scratchMid.copy(from).add(to).multiplyScalar(0.5);
        const outward = this._scratchMid.clone().normalize().multiplyScalar(lift * 0.25);
        const mid = this._scratchMid.clone().add(new ThreeVector3(0, lift, 0)).add(outward);
        return new CatmullRomCurve3([from.clone(), mid, to.clone()], false, 'catmullrom', 0.5);
    }

    /** Cubic ease-in-out: gentle departure, gentle arrival, no overshoot. */
    private static ease(t: number): number {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    /**
     * Advance the transition and write the camera pose.
     *
     * @returns true when the choreographer handled this frame (caller must NOT apply its
     *          own smoothing), false when it is idle.
     */
    public apply(
        camera: PerspectiveCamera,
        desiredPosition: ThreeVector3,
        desiredQuaternion: ThreeQuaternion,
        deltaTimeSec: number,
        lookAtTarget?: ThreeVector3
    ): boolean {
        if (!this._active) return false;

        this._elapsedMs += deltaTimeSec * 1000;
        const raw = Math.min(1, this._elapsedMs / this._durationMs);
        const t = CameraChoreographer.ease(raw);

        if (this._curve) {
            // Re-target the curve's end each frame so a moving destination stays tracked
            // without restarting the transition.
            const pts = (this._curve as any).points as ThreeVector3[];
            pts[pts.length - 1].copy(desiredPosition);
            this._curve.getPoint(t, this._scratchPos);
            camera.position.copy(this._scratchPos);
        } else {
            camera.position.lerpVectors(this._startPos, desiredPosition, t);
        }

        // Die Kamera muss WÄHREND der Fahrt auf ihr Ziel blicken. Eine sture Interpolation
        // zwischen Start- und Zielorientierung lässt sie an ihrem Ziel vorbeischauen und
        // wirkt dadurch billig. Deshalb: Blickrichtung aus der aktuellen (interpolierten)
        // Position auf den Zielpunkt, nur die Restdrehung wird geglättet.
        if (lookAtTarget) {
            this._scratchQuat.setFromRotationMatrix(
                this._scratchMat.lookAt(camera.position, lookAtTarget, this._up)
            );
            // Am Anfang etwas Start-Orientierung beimischen, damit der Blick nicht springt.
            const blend = Math.min(1, t * 2.2);
            camera.quaternion.slerpQuaternions(this._startQuat, this._scratchQuat, blend);
        } else {
            camera.quaternion.slerpQuaternions(this._startQuat, desiredQuaternion, t);
        }

        if (this._preset === 'blend' && raw >= BLEND_HANDOFF) {
            // Uebergabe ans Smoothing: kein Schnappen auf die Zielpose, den Rest der
            // Strecke erledigt das gewohnte asymptotische Nachziehen.
            this._active = false;
            this._curve = null;
            Logger.Instance.Info(`transition handed to smoothing at ${(raw * 100).toFixed(0)}% (${this._pathMode})`, 'Choreographer');
            return true;
        }

        if (raw >= 1) {
            // Deterministic end: land exactly on the destination, then hand back to the
            // normal smoothing path.
            camera.position.copy(desiredPosition);
            camera.quaternion.copy(desiredQuaternion);
            this._active = false;
            this._curve = null;
            Logger.Instance.Info(`transition end after ${this._elapsedMs.toFixed(0)}ms (${this._pathMode})`, 'Choreographer');
        }
        return true;
    }

    /** Abort without snapping — the camera stays where it is and smoothing takes over. */
    public cancel(): void {
        this._active = false;
        this._curve = null;
    }
}
